/**
 * `/q/:token` — the buyer-facing page opened from a shared-Quotation link.
 * This page IS the PDF: staff and buyers print it straight from the browser,
 * there is no PDF generator. A sibling of `s.$token.tsx` and `login.tsx`,
 * never under `routes/_app/`: no session, no offline machinery, no app
 * shell. A quotation is a priced document, not an interaction — no shortlist,
 * no tick boxes, no quantity inputs; quantities and prices are fixed and
 * arrive already computed from the server (never recomputed here).
 */
import { ErrorEnvelope, formatDimensions, formatMoney, formatWeight, SharedQuotation } from '@paradise/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { formatDocumentDate } from '@/components/print/document-date';
import { DocumentTerms } from '@/components/print/document-terms';
import { PrintButton } from '@/components/print/print-button';
import { PrintFooter, PrintHeader } from '@/components/print/print-header';
import { PrintItemsTable, type PrintTableRow } from '@/components/print/print-items-table';
import '@/components/print/print.css';
import { skuCountLabel } from '@/components/print/report-summary';
import { Button } from '@/components/ui/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';

export const Route = createFileRoute('/q/$token')({
  component: SharedQuotationPage
});

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; data: SharedQuotation }
  | { kind: 'expired' }
  | { kind: 'revoked' }
  | { kind: 'error' };

// `details` isn't part of the shared `ErrorBody` shape (it's the 410 route's
// own payload), so it's validated here rather than cast.
const GoneDetails = z.object({ reason: z.enum(['revoked', 'expired']) });

function goneReason(details: unknown): 'revoked' | 'expired' | null {
  const parsed = GoneDetails.safeParse(details);
  return parsed.success ? parsed.data.reason : null;
}

async function loadQuotation(token: string): Promise<LoadState> {
  let response: Response;
  try {
    response = await fetch(`/api/q/${encodeURIComponent(token)}`);
  } catch {
    return { kind: 'error' };
  }

  if (response.status === 200) {
    const body: unknown = await response.json().catch(() => null);
    const parsed = SharedQuotation.safeParse(body);
    return parsed.success ? { kind: 'ok', data: parsed.data } : { kind: 'error' };
  }

  if (response.status === 410) {
    const body: unknown = await response.json().catch(() => null);
    const envelope = ErrorEnvelope.safeParse(body);
    const reason = envelope.success ? goneReason(envelope.data.error.details) : null;
    return reason === 'expired' ? { kind: 'expired' } : { kind: 'revoked' };
  }

  return { kind: 'error' };
}

function SharedQuotationPage() {
  const { token } = Route.useParams();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  // Optimistic only after a 200. The server is idempotent, so a reopened link
  // reads its real status and this flag is just what avoids a refetch.
  const [acceptedNow, setAcceptedNow] = useState(false);

  async function accept(): Promise<void> {
    setAccepting(true);
    setAcceptError(null);
    try {
      const response = await fetch(`/api/q/${encodeURIComponent(token)}/accept`, { method: 'POST' });
      if (response.ok) setAcceptedNow(true);
      else {
        const body: unknown = await response.json().catch(() => null);
        const envelope = ErrorEnvelope.safeParse(body);
        setAcceptError(envelope.success ? envelope.data.error.message : 'Could not send that. Please try again.');
      }
    } catch {
      setAcceptError('Could not reach us. Check your connection and try again.');
    } finally {
      setAccepting(false);
    }
  }

  useEffect(() => {
    void loadQuotation(token).then(setState);
  }, [token]);

  if (state.kind === 'loading') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
      </main>
    );
  }

  if (state.kind === 'expired') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center p-6">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>This link has expired</EmptyTitle>
            <EmptyDescription>Please ask your contact at The Paradise for a fresh document.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </main>
    );
  }

  if (state.kind === 'revoked') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center p-6">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>This link is no longer available</EmptyTitle>
            <EmptyDescription>Please contact The Paradise if you need this document again.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center p-6">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Could not load</EmptyTitle>
            <EmptyDescription>Something went wrong. Please reload the page or try again shortly.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </main>
    );
  }

  const { data } = state;
  const accepted = acceptedNow || data.status === 'ACCEPTED';
  // Prefilled so the buyer does not have to explain which document they mean —
  // a bare chat window would arrive as "hi" and cost a round trip to identify.
  const revisionHref =
    data.contactWhatsapp === null
      ? null
      : `https://wa.me/${data.contactWhatsapp}?text=${encodeURIComponent(
          `Hi, this is ${data.recipientName} from ${data.recipientCompany}. About quotation ${data.docRef} — could we look at a change?`
        )}`;

  const rows: PrintTableRow[] = data.items.map(item => ({
    itemNo: item.itemNo,
    name: item.name,
    quantity: item.quantity,
    unitPrice: item.unitPrice,
    lineTotal: item.lineTotal,
    note: item.note
  }));
  const photos = new Map(data.items.map(item => [String(item.itemNo), item.coverKeys[0] ?? null]));
  const measures = new Map<string, string>();
  for (const item of data.items) {
    const parts = [formatDimensions(item, item.dimensionUnit), formatWeight(item.weightGrams)].filter(
      (part): part is string => part !== null
    );
    if (parts.length > 0) measures.set(String(item.itemNo), parts.join(' · '));
  }

  return (
    <main className="flex min-h-dvh flex-col gap-6 pb-10 print:pb-0">
      <div className="mx-auto w-full max-w-[210mm] px-6 text-foreground print:px-0">
        <PrintHeader
          title="Quotation"
          // Through the shared helper, so the buyer's copy and the staff print
          // of the same quotation are always dated identically.
          subtitle={`${data.docRef}${data.revision > 1 ? ` · Revision ${data.revision}` : ''} · ${formatDocumentDate(data.createdAt)} · theparadise.co.in`}
        />

        <div className="mt-3.5 flex justify-end print:hidden">
          <PrintButton label="Print or save as PDF" />
        </div>

        <div className="mt-4">
          <div className="text-[10px] font-medium tracking-wider text-primary uppercase">Prepared for</div>
          <div className="font-heading text-xl font-semibold">{data.recipientCompany}</div>
          <div className="text-[12.5px] text-muted-foreground">{data.recipientName}</div>
          {data.expiresAt && (
            <div className="mt-1 text-[12.5px] text-muted-foreground">
              Valid until{' '}
              {new Date(data.expiresAt).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
            </div>
          )}
        </div>

        <div className="mt-4">
          <PrintItemsTable
            items={rows}
            currency={data.currency}
            photos={photos}
            photoUrl={key => `/api/q/${encodeURIComponent(token)}/media/${encodeURIComponent(key)}`}
            measures={measures}
          />
        </div>

        <div className="mt-2.5 flex items-baseline justify-between border-t-2 border-primary pt-2.5">
          <span className="text-sm font-medium">
            Total
            <span className="ml-2 text-[12.5px] font-normal text-muted-foreground">{skuCountLabel(rows.length)}</span>
          </span>
          <span className="font-heading text-xl font-semibold whitespace-nowrap">
            {formatMoney(data.total, data.currency)}
          </span>
        </div>

        <DocumentTerms terms={data.terms} />

        {/* Replaces "reply to whoever sent you this". Acceptance was a message
            somebody had to read, believe and re-key; a revision was the same
            message with different words. Both are buttons now.

            Screen only: a printed copy is read next to the message it arrived
            with, and a printed button is a dead thing on paper. */}
        <div className="mt-5 print:hidden">
          {accepted ? (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3.5">
              <div className="text-[10px] font-medium tracking-wider text-primary uppercase">Accepted</div>
              <p className="mt-1 text-sm">
                Thank you. {data.senderName ?? 'The Paradise'} has been told, and will be in touch to confirm the order.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3.5">
              <div className="text-[10px] font-medium tracking-wider text-primary uppercase">Your decision</div>
              <p className="mt-1 text-sm">
                Accepting tells {data.senderName ?? 'The Paradise'} to go ahead. If something needs changing — a
                quantity, a finish, the price — ask for a revision instead.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button type="button" disabled={accepting} onClick={() => void accept()}>
                  {accepting ? 'Sending…' : 'Accept this quotation'}
                </Button>
                {revisionHref !== null && (
                  <Button
                    variant="outline"
                    render={<a href={revisionHref} target="_blank" rel="noopener noreferrer" />}
                  >
                    Ask for a change
                  </Button>
                )}
              </div>
              {!revisionHref && (
                <p className="mt-2 text-xs text-muted-foreground">
                  To ask for a change, reply on the message that brought you this link.
                </p>
              )}
              {acceptError !== null && <p className="mt-2 text-sm text-destructive">{acceptError}</p>}
            </div>
          )}
        </div>

        <PrintFooter left="The Paradise · theparadise.co.in" right={`Prices in ${data.currency}`} />
      </div>
    </main>
  );
}
